import React, { useEffect, useState } from 'react';
import { Disclosure } from '@headlessui/react';
import { FunnelIcon } from '@heroicons/react/20/solid';
import DatePicker from 'react-datepicker';

import FilterVideos from '../../entities/filter/videos';
import { SimpleObject } from '../../entities/simple-object';
import { strings } from '../../localization/strings';
import { useDebounce } from '../../hooks/use-debounce';
import { fromJsDate } from '../../services/formatter/date';
import { pluralizeWithNumber } from '../../services/formatter/pluralize';
import { createDateKeyDownHandler } from '../../utils/date-input-handler';

export default function VideosFilter({
  onUpdate,
  initialState,
  filterState,
}: {
  onUpdate: (filters: FilterVideos) => void;
  initialState: FilterVideos;
  filterState: FilterVideos;
}) {
  const [search, setSearch] = useState(filterState.search || '');
  const [dateValue, setDateValue] = useState<SimpleObject<Date | null>>({});
  const activeFilters = [filterState.search, filterState.created_at_from, filterState.created_at_to].filter(
    Boolean,
  ).length;

  useEffect(() => {
    setSearch(filterState.search || '');
    setDateValue({
      created_at_from: filterState.created_at_from instanceof Date ? filterState.created_at_from : null,
      created_at_to: filterState.created_at_to instanceof Date ? filterState.created_at_to : null,
    });
  }, [filterState]);

  const onDelayedTextChange = useDebounce((value: string) => {
    onUpdate({ ...filterState, search: value });
  }, 500);

  const onChangeDate = (date: [Date | null, Date | null]) => {
    setDateValue({ created_at_from: date[0], created_at_to: date[1] });
    onUpdate({
      ...filterState,
      created_at_from: date[0] ? fromJsDate(date[0]) : null,
      created_at_to: date[1] ? fromJsDate(date[1]) : null,
    });
  };

  return (
    <Disclosure as="section" className="border-b border-gray-200 bg-white sm:grid sm:items-center">
      <div className="relative col-start-1 row-start-1 py-4">
        <div className="mx-auto flex max-w-7xl space-x-6 divide-x divide-gray-200 px-4 text-sm sm:px-6 lg:px-8">
          <div>
            <Disclosure.Button className="group flex items-center font-medium text-gray-700">
              <FunnelIcon
                className="mr-2 h-5 w-5 flex-none text-gray-400 group-hover:text-gray-500"
                aria-hidden="true"
              />
              {activeFilters
                ? pluralizeWithNumber(activeFilters, strings.companies.filter.activeFilters)
                : strings.companies.filter.select}
            </Disclosure.Button>
          </div>
          <div className="pl-6">
            {activeFilters > 0 && (
              <button type="button" className="text-gray-500" onClick={() => onUpdate({ ...initialState })}>
                {strings.filter.clearFilter}
              </button>
            )}
          </div>
        </div>
      </div>
      <Disclosure.Panel className="border-t border-gray-200 py-5">
        <div className="mx-auto max-w-7xl px-4 text-sm sm:px-6 lg:px-8">
          <fieldset className="w-72 max-w-full">
            <label htmlFor="video-date-range" className="block font-medium">
              {strings.common.filter.dateRange}
            </label>
            <div className="mt-4">
              <DatePicker
                id="video-date-range"
                monthsShown={2}
                showYearDropdown
                dateFormat="yyyy-MM-dd"
                isClearable
                selectsRange
                wrapperClassName="w-full"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                startDate={dateValue.created_at_from}
                selected={dateValue.created_at_from}
                endDate={dateValue.created_at_to}
                onChange={onChangeDate}
                onKeyDown={createDateKeyDownHandler(
                  'created_at_from',
                  'created_at_to',
                  dateValue,
                  filterState,
                  setDateValue,
                  onUpdate,
                )}
              />
            </div>
          </fieldset>
        </div>
      </Disclosure.Panel>
      <div className="col-start-1 row-start-1 py-4">
        <div className="mx-auto flex max-w-7xl justify-end px-4 sm:px-6 lg:px-8">
          <div className="relative w-full sm:w-96">
            <input
              type="search"
              aria-label={strings.common.searchLabel}
              placeholder={strings.common.searchLabel}
              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-500 focus:border-rose-500 focus:text-gray-900 focus:outline-none focus:ring-1 focus:ring-rose-500 focus:placeholder-gray-400 sm:text-sm"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                onDelayedTextChange(event.target.value);
              }}
            />
          </div>
        </div>
      </div>
    </Disclosure>
  );
}
